import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { format, isSameDay, startOfMonth, startOfQuarter, startOfYear, subDays } from 'date-fns';
import { CalendarRange } from 'lucide-react';
import { useListChampionshipsQuery } from 'modules/championship/store/championships-api';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SingleValue } from 'react-select';
import { UDDateRangeInput, UDDateRangeValue } from 'ud-form/components/datetime/component';
import UDFormLabel from 'ud-form/components/label';
import { UDSelect } from 'ud-form/components/select/component';
import { SelectOption } from 'ud-form/components/select/component/types';
import UDButton from 'ud-ui/components/button';

interface AnalyticsFiltersProps {
  tournamentId: number;
  onFiltersChange: (filters: { championshipId?: number | null; startDate: string; endDate: string }) => void;
}

interface Period {
  start: Date;
  end: Date;
}

const DATE_PRESETS = [
  { label: msg`За последнюю неделю`, getStart: () => subDays(new Date(), 7) },
  { label: msg`За месяц`, getStart: () => startOfMonth(new Date()) },
  { label: msg`За квартал`, getStart: () => startOfQuarter(new Date()) },
  { label: msg`За год`, getStart: () => startOfYear(new Date()) },
] as const;

const AnalyticsFilters = React.memo(({ tournamentId, onFiltersChange }: AnalyticsFiltersProps) => {
  const { t } = useLingui();
  const today = useMemo(() => new Date(), []);
  const allChampionshipsOption = useMemo<SelectOption<number | null>>(() => ({ label: t`Все чемпионаты`, value: null }), [t]);
  const [selectedChampionshipId, setSelectedChampionshipId] = useState<number | null>(null);
  // Применённый период — по нему строятся запросы. Черновик — то, что сейчас в полях: пока выбрано только
  // начало, он неполный и в запрос не уходит, иначе каждый клик по календарю дёргал бы статистику.
  const [period, setPeriod] = useState<Period>(() => ({ start: subDays(new Date(), 7), end: new Date() }));
  const [draft, setDraft] = useState<UDDateRangeValue>(period);
  const { start: startDate, end: endDate } = period;

  const { data: championships, isFetching: isChampionshipsFetching } = useListChampionshipsQuery({
    tournamentId,
    limit: 100,
    offset: 0,
    afterDate: format(startDate, 'yyyy-MM-dd'),
    beforeDate: format(endDate, 'yyyy-MM-dd'),
  });

  useEffect(() => {
    if (selectedChampionshipId === null) return;
    if (isChampionshipsFetching) return;
    const stillPresent = championships?.some((c) => c.id === selectedChampionshipId);
    if (!stillPresent) {
      setSelectedChampionshipId(null);
    }
  }, [championships, isChampionshipsFetching, selectedChampionshipId]);

  const championshipOptions = useMemo<SelectOption<number | null>[]>(() => {
    const items: SelectOption<number | null>[] = [allChampionshipsOption];
    championships?.forEach((c) => items.push({ label: c.name, value: c.id }));
    return items;
  }, [allChampionshipsOption, championships]);

  const selectedOption = useMemo(
    () => championshipOptions.find((o) => o.value === selectedChampionshipId) ?? allChampionshipsOption,
    [allChampionshipsOption, championshipOptions, selectedChampionshipId],
  );

  const handleChampionshipChange = useCallback((option: SingleValue<SelectOption<number | null>>) => {
    setSelectedChampionshipId(option?.value ?? null);
  }, []);

  const handleRangeChange = useCallback(
    (next: UDDateRangeValue) => {
      setDraft(next);
      const { start, end } = next;
      if (!start || !end) return;
      if (isSameDay(start, startDate) && isSameDay(end, endDate)) return;

      setPeriod({ start, end });
      setSelectedChampionshipId(null);
    },
    [startDate, endDate],
  );

  // Календарь конца закрыли, не выбрав дату (Esc, клик мимо): поля возвращаются к применённому периоду,
  // иначе в «По» пусто, а цифры на экране — за прежний период.
  const handleEndCalendarClose = useCallback(() => {
    setDraft((current) => (current.start && current.end ? current : period));
  }, [period]);

  const handlePresetClick = useCallback((getStart: () => Date) => {
    const next = { start: getStart(), end: new Date() };
    setPeriod(next);
    setDraft(next);
    setSelectedChampionshipId(null);
  }, []);

  const activePresetIndex = useMemo(() => {
    const { start, end } = draft;
    if (!start || !end || !isSameDay(end, new Date())) return -1;
    const expectedStarts = [subDays(end, 7), startOfMonth(end), startOfQuarter(end), startOfYear(end)];
    return expectedStarts.findIndex((expectedStart) => isSameDay(start, expectedStart));
  }, [draft]);

  useEffect(() => {
    onFiltersChange({
      championshipId: selectedChampionshipId,
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
    });
  }, [selectedChampionshipId, startDate, endDate, onFiltersChange]);

  return (
    <div className="border-lineSubtle bg-surfaceCard rounded-panel flex flex-col gap-4 border p-5">
      <div className="flex flex-wrap items-end gap-4">
        <div className="min-w-[260px] flex-1">
          <UDFormLabel className="mb-2">
            <Trans>Чемпионат</Trans>
          </UDFormLabel>
          <UDSelect options={championshipOptions} value={selectedOption} onChange={handleChampionshipChange} />
        </div>
        <div className="w-80 shrink-0">
          <UDFormLabel className="mb-2">
            <Trans>Период</Trans>
          </UDFormLabel>
          <UDDateRangeInput
            startDate={draft.start}
            endDate={draft.end}
            onChange={handleRangeChange}
            maxDate={today}
            restartOnStartSelect
            endProps={{ onCalendarClose: handleEndCalendarClose }}
          />
        </div>
      </div>
      <div className="border-lineSubtle flex flex-wrap items-center gap-2 border-t pt-4">
        <CalendarRange size={16} className="text-inkMuted mr-1" />
        {DATE_PRESETS.map((preset, index) => (
          <UDButton
            key={preset.label.id}
            variant={activePresetIndex === index ? 'primary' : 'light'}
            slim
            aria-pressed={activePresetIndex === index}
            onClick={() => handlePresetClick(preset.getStart)}
          >
            {t(preset.label)}
          </UDButton>
        ))}
      </div>
    </div>
  );
});

export default AnalyticsFilters;
